import { NormalizedNumber, Opaque } from '@sparkdotfi/common-universal'
import { QueryKey, useSuspenseQuery } from '@tanstack/react-query'
import { arbitrum, base, optimism, unichain } from 'viem/chains'
import { Config, useConfig } from 'wagmi'
import { z } from 'zod'

import { SwapFormSchema } from './schema'
import { getSwapFormValidator, swapValidationIssueToMessage, validateSwapWithPsm3 } from './validator'
import { TokenRepository } from '@/domain/token-repository/TokenRepository'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { psm3Balances } from '@/features/dialogs/common/logic/psm3BalancesQuery'

export type SwapValidator = z.ZodSchema<{
  from: string
  to: string
  amount: string
  isMaxSelected?: boolean | undefined
}>

export interface UseSwapValidatorParams {
  chainId: number
  tokenRepository: TokenRepository
}

export function useSwapValidator({ chainId, tokenRepository }: UseSwapValidatorParams): SwapValidator {
  const wagmiConfig = useConfig()

  const { fetchParamsQueryKey, fetchParamsQueryFn, createValidator } = getValidatorConfig({
    chainId,
    tokenRepository,
    wagmiConfig,
  })

  const { data } = useSuspenseQuery({
    queryKey: fetchParamsQueryKey,
    queryFn: fetchParamsQueryFn,
  })

  return createValidator(data)
}

interface GetValidatorConfigParams {
  chainId: number
  tokenRepository: TokenRepository
  wagmiConfig: Config
}
function getValidatorConfig({
  chainId,
  tokenRepository,
  wagmiConfig,
}: GetValidatorConfigParams): DynamicValidatorConfigOpaque {
  if (chainId === base.id || chainId === arbitrum.id || chainId === optimism.id || chainId === unichain.id) {
    const usdc = tokenRepository.findOneBySymbol(TokenSymbol('USDC'))
    const validatorQuery = psm3Balances({ tokenRepository, wagmiConfig, chainId })

    return ensureDynamicValidatorConfigTypes({
      fetchParamsQueryKey: validatorQuery.queryKey,
      fetchParamsQueryFn: validatorQuery.queryFn,
      createValidator: ({ usds: usdsBalance, usdc: usdcBalance }) =>
        SwapFormSchema.superRefine((field, ctx) => {
          const value = NormalizedNumber(field.amount === '' ? '0' : field.amount)
          const isSwapToUsdc = field.to === usdc.symbol
          const inTokenBalance = tokenRepository.findOneBalanceOrZero(TokenSymbol(field.from))

          const issue = validateSwapWithPsm3({
            value,
            isSwapToUsdc,
            user: { balance: inTokenBalance },
            psm3: {
              usdsBalance,
              usdcBalance,
            },
          })
          if (issue) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: swapValidationIssueToMessage[issue],
              path: ['amount'],
            })
          }
        }),
    })
  }

  return ensureDynamicValidatorConfigTypes({
    fetchParamsQueryKey: getCreateValidatorConfigQueryKey(chainId),
    fetchParamsQueryFn: () => Promise.resolve({}),
    createValidator: () => getSwapFormValidator(tokenRepository),
  })
}

function getCreateValidatorConfigQueryKey(chainId: number): QueryKey {
  return [chainId, 'dynamic-validator-swap']
}

interface DynamicValidatorConfig<T> {
  fetchParamsQueryKey: QueryKey
  fetchParamsQueryFn: () => Promise<T>
  createValidator: (params: T) => SwapValidator
}

type DynamicValidatorConfigOpaque = Opaque<DynamicValidatorConfig<any>, 'DynamicValidatorConfig'>

function ensureDynamicValidatorConfigTypes<T extends {}>(
  config: DynamicValidatorConfig<T>,
): DynamicValidatorConfigOpaque {
  return config as DynamicValidatorConfigOpaque
}
